// Armado puro de las respuestas del perfil de PLD que captura ProfileQuestions.
import type {
    OccupationProfileFields,
    SetOccupationInput,
} from '@/api/services/retail-wizard/contract';
import type { MonthlyIncome, Occupation } from '../types';
import { PROFILE_QUESTION_OPTIONS, type ProfileQuestionKey } from './ProfileQuestions';

/** Respuestas de los 4 MXN_* tal como llegan de los selects. */
export type ProfileAnswers = Partial<Record<ProfileQuestionKey, string>>;

const OPENING_ONLY_KEYS = [
    'monthlyExtraSupport',
    'usageMonthly',
    'maximumTx',
    'beneficialOwner',
] as const satisfies readonly (keyof OccupationProfileFields)[];

/** ¿Están contestados los 4 MXN_* que la apertura exige? */
export function openingAnswersComplete(answers: ProfileAnswers): boolean {
    return OPENING_ONLY_KEYS.every((k) => !!answers[k]);
}

/** Valor que pinta cada pregunta: ocupación e ingreso viven aparte de los MXN_*. */
export function profileAnswerOf(
    key: ProfileQuestionKey,
    current: { occupation: Occupation | ''; income: MonthlyIncome | ''; answers: ProfileAnswers },
): string {
    if (key === 'occupation') return current.occupation;
    if (key === 'monthlyIncome') return current.income;
    return current.answers[key] ?? '';
}

/** Devuelve la respuesta solo si es una opción del catálogo de esa pregunta. */
function catalogValue<T extends string>(
    options: readonly (readonly [T, string])[],
    answer: string | undefined,
): T | undefined {
    return options.find(([value]) => value === answer)?.[0];
}

function profileFieldsOf(answers: ProfileAnswers): OccupationProfileFields {
    return {
        monthlyExtraSupport: catalogValue(
            PROFILE_QUESTION_OPTIONS.monthlyExtraSupport,
            answers.monthlyExtraSupport,
        ),
        usageMonthly: catalogValue(PROFILE_QUESTION_OPTIONS.usageMonthly, answers.usageMonthly),
        maximumTx: catalogValue(PROFILE_QUESTION_OPTIONS.maximumTx, answers.maximumTx),
        beneficialOwner: catalogValue(
            PROFILE_QUESTION_OPTIONS.beneficialOwner,
            answers.beneficialOwner,
        ),
    };
}

/** Body de `POST …/occupation`; la apertura agrega los 4 MXN_*. */
export function toSetOccupationInput(input: {
    occupation: Occupation;
    otherText: string;
    income: MonthlyIncome;
    answers: ProfileAnswers;
    accountOpening: boolean;
}): SetOccupationInput {
    return {
        code: input.occupation,
        otherText: input.occupation === 'OTHER' ? input.otherText.trim() : undefined,
        monthlyIncome: input.income,
        ...(input.accountOpening && profileFieldsOf(input.answers)),
    };
}
